import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { toast } from '../lib/toast';
import { subscriptionManager } from './subscriptionManager';
import { APIClient } from './api/apiClient';

interface SessionConfig {
  refreshThreshold: number; // Minutes before expiry to refresh
  checkInterval: number; // Interval to check session validity (ms)
  maxRetries: number;
  onSessionExpired?: () => void;
  onSessionRefreshed?: (session: Session) => void;
  onUserUpdated?: (user: User) => void;
}

interface SessionState {
  session: Session | null;
  user: User | null;
  isRefreshing: boolean;
  lastRefresh: Date | null;
  refreshCount: number;
  errors: Error[];
}

/**
 * Manages user sessions, token refresh, and authentication state
 */
export class SessionManager {
  private static instance: SessionManager | null = null;
  private config: SessionConfig;
  private state: SessionState;
  private checkTimer: NodeJS.Timeout | null = null;
  private refreshPromise: Promise<Session | null> | null = null;
  private activityTimer: NodeJS.Timeout | null = null;
  private lastActivity: Date = new Date();
  private warningShown = false;

  private constructor(config: Partial<SessionConfig> = {}) {
    this.config = {
      refreshThreshold: 5, // Refresh 5 minutes before expiry
      checkInterval: 60000, // Check every minute
      maxRetries: 3,
      ...config
    };

    this.state = {
      session: null,
      user: null,
      isRefreshing: false,
      lastRefresh: null,
      refreshCount: 0,
      errors: []
    };

    this.setupActivityTracking();
    this.setupAuthListener();
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<SessionConfig>): SessionManager {
    if (!this.instance) {
      this.instance = new SessionManager(config);
    }
    return this.instance;
  }

  /**
   * Initialize session manager
   */
  async initialize(): Promise<boolean> {
    try {
      // Get initial session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('Failed to get initial session', { error });
        return false;
      }

      if (session) {
        this.state.session = session;
        this.state.user = session.user;
        
        // Start session monitoring
        this.startSessionCheck();
        
        logger.info('Session manager initialized', {
          userId: session.user.id,
          email: session.user.email,
          expiresAt: new Date(session.expires_at! * 1000)
        });
        
        return true;
      }

      logger.info('No active session found');
      return false;
    } catch (error) {
      logger.error('Session initialization failed', { error });
      return false;
    }
  }

  /**
   * Setup auth state change listener
   */
  private setupAuthListener(): void {
    supabase.auth.onAuthStateChange(async (event, session) => {
      logger.info('Auth state changed', { event });

      switch (event) {
        case 'SIGNED_IN':
          this.handleSignIn(session!);
          break;
          
        case 'SIGNED_OUT':
          this.handleSignOut();
          break;
          
        case 'TOKEN_REFRESHED':
          this.handleTokenRefreshed(session!);
          break;
          
        case 'USER_UPDATED':
          this.handleUserUpdated(session!);
          break;
          
        case 'PASSWORD_RECOVERY':
          logger.info('Password recovery initiated');
          break;
      }
    });
  }

  /**
   * Handle sign in
   */
  private async handleSignIn(session: Session): Promise<void> {
    this.state.session = session;
    this.state.user = session.user;
    this.state.errors = [];
    this.warningShown = false;
    
    // Start session monitoring
    this.startSessionCheck();
    
    logger.info('User signed in', {
      userId: session.user.id,
      email: session.user.email
    });
  }

  /**
   * Handle sign out
   */
  private async handleSignOut(): Promise<void> {
    this.state.session = null;
    this.state.user = null;
    
    // Stop monitoring
    this.stopSessionCheck();
    
    // Clear all subscriptions
    await subscriptionManager.unsubscribeAll();
    
    // Clear API cache
    APIClient.clearCache();
    
    logger.info('User signed out');
    
    // Notify callback
    if (this.config.onSessionExpired) {
      this.config.onSessionExpired();
    }
  }

  /**
   * Handle token refresh
   */
  private handleTokenRefreshed(session: Session): void {
    this.state.session = session;
    this.state.lastRefresh = new Date();
    this.state.refreshCount++;
    this.state.isRefreshing = false;
    this.warningShown = false;
    
    logger.info('Token refreshed', {
      refreshCount: this.state.refreshCount,
      expiresAt: new Date(session.expires_at! * 1000)
    });
    
    // Notify callback
    if (this.config.onSessionRefreshed) {
      this.config.onSessionRefreshed(session);
    }
  }

  /**
   * Handle user update
   */
  private handleUserUpdated(session: Session): void {
    this.state.session = session;
    this.state.user = session.user;
    
    logger.info('User updated', {
      userId: session.user.id,
      email: session.user.email
    });
    
    // Notify callback
    if (this.config.onUserUpdated) {
      this.config.onUserUpdated(session.user);
    }
  }

  /**
   * Start session validity checking
   */
  private startSessionCheck(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
    }

    this.checkTimer = setInterval(() => {
      this.checkSession();
    }, this.config.checkInterval);

    // Also check immediately
    this.checkSession();
  }

  /**
   * Stop session checking
   */
  private stopSessionCheck(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }

  /**
   * Check session validity and refresh if needed
   */
  private async checkSession(): Promise<void> {
    if (!this.state.session) return;

    const now = Date.now();
    const expiresAt = this.state.session.expires_at! * 1000;
    const timeUntilExpiry = expiresAt - now;
    const refreshThreshold = this.config.refreshThreshold * 60 * 1000;

    // Check if session needs refresh
    if (timeUntilExpiry <= refreshThreshold) {
      if (timeUntilExpiry <= 60000 && !this.warningShown) {
        // Show warning when less than 1 minute left
        toast.warning('Your session will expire soon. Refreshing...');
        this.warningShown = true;
      }
      
      await this.refreshSession();
    }

    // Check for inactivity
    const inactivityLimit = 30 * 60 * 1000; // 30 minutes
    const timeSinceActivity = now - this.lastActivity.getTime();
    
    if (timeSinceActivity > inactivityLimit) {
      logger.info('User inactive for too long');
      toast.info('You have been inactive. Please sign in again for security.');
      await this.signOut();
    }
  }

  /**
   * Refresh the session token
   */
  async refreshSession(): Promise<Session | null> {
    // Prevent concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.state.isRefreshing = true;
    
    this.refreshPromise = this.performRefresh();
    
    try {
      const session = await this.refreshPromise;
      return session;
    } finally {
      this.refreshPromise = null;
      this.state.isRefreshing = false;
    }
  }

  /**
   * Perform the actual refresh
   */
  private async performRefresh(): Promise<Session | null> {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < this.config.maxRetries; attempt++) {
      try {
        logger.info(`Refreshing session (attempt ${attempt + 1})`);
        
        const { data: { session }, error } = await supabase.auth.refreshSession();
        
        if (error) throw error;
        
        if (session) {
          this.handleTokenRefreshed(session);
          return session;
        }
        
        throw new Error('No session returned after refresh');
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        logger.error('Session refresh failed', { 
          attempt: attempt + 1, 
          error: lastError.message 
        });
        
        // Wait before retrying (exponential backoff)
        if (attempt < this.config.maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    this.state.errors.push(lastError!);
    logger.error('Failed to refresh session after retries');
    toast.error('Session refresh failed. Please sign in again.');
    
    // Force sign out
    await this.signOut();
    
    return null;
  }

  /**
   * Setup activity tracking
   */
  private setupActivityTracking(): void {
    const updateActivity = () => {
      this.lastActivity = new Date();
    };

    // Track various user activities
    window.addEventListener('click', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);
  }

  /**
   * Get current session
   */
  getSession(): Session | null {
    return this.state.session;
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    return this.state.user;
  }

  /**
   * Check if session is valid
   */
  isValid(): boolean {
    if (!this.state.session) return false;
    
    const now = Date.now();
    const expiresAt = this.state.session.expires_at! * 1000;
    
    return now < expiresAt;
  }

  /**
   * Get time until session expiry
   */
  getTimeUntilExpiry(): number {
    if (!this.state.session) return 0;
    
    const now = Date.now();
    const expiresAt = this.state.session.expires_at! * 1000;
    
    return Math.max(0, expiresAt - now);
  }

  /**
   * Sign out the user
   */
  async signOut(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error('Sign out failed', { error });
        throw error;
      }
    } catch (error) {
      logger.error('Sign out error', { error });
      
      // Force local sign out even if server fails
      this.handleSignOut();
    }
  }

  /**
   * Update user metadata
   */
  async updateUser(updates: {
    email?: string;
    password?: string;
    data?: object;
  }): Promise<User | null> {
    try {
      const { data: { user }, error } = await supabase.auth.updateUser(updates);
      
      if (error) throw error;
      
      if (user) {
        this.state.user = user;
        logger.info('User updated successfully');
        return user;
      }
      
      return null;
    } catch (error) {
      logger.error('Failed to update user', { error });
      toast.error('Failed to update profile');
      return null;
    }
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<boolean> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
      
      toast.success('Password reset email sent');
      return true;
    } catch (error) {
      logger.error('Password reset request failed', { error });
      toast.error('Failed to send password reset email');
      return false;
    }
  }

  /**
   * Verify OTP
   */
  async verifyOTP(email: string, token: string): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });
      
      if (error) throw error;
      
      if (session) {
        await this.handleSignIn(session);
        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('OTP verification failed', { error });
      toast.error('Invalid or expired code');
      return false;
    }
  }

  /**
   * Get session statistics
   */
  getStatistics(): {
    isActive: boolean;
    isRefreshing: boolean;
    refreshCount: number;
    timeUntilExpiry: number;
    lastActivity: Date;
    errors: number;
  } {
    return {
      isActive: this.isValid(),
      isRefreshing: this.state.isRefreshing,
      refreshCount: this.state.refreshCount,
      timeUntilExpiry: this.getTimeUntilExpiry(),
      lastActivity: this.lastActivity,
      errors: this.state.errors.length
    };
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    this.stopSessionCheck();
    
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    
    SessionManager.instance = null;
  }
}

// Export singleton instance with lazy initialization to avoid TDZ errors
let _sessionManager: SessionManager | null = null;
const getSessionManager = (): SessionManager => {
  if (!_sessionManager) {
    _sessionManager = SessionManager.getInstance();
  }
  return _sessionManager;
};

// Use Proxy to forward all property access and method calls to the instance
export const sessionManager = new Proxy({} as SessionManager, {
  get(_target, prop) {
    const instance = getSessionManager();
    const value = (instance as any)[prop];
    // If it's a function, bind it to the instance
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getSessionManager();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getSessionManager();
    return prop in instance;
  },
});
